import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import {
  ProcessPaymentResponse,
  BraintreeConfig,
  BraintreeExecuteResult,
  PaypalConfig,
  PaypalCreateResult,
  PaypalExecuteResult,
  MultibancoConfig,
  CieloConfig,
  BoletoConfig,
  BankTransferConfig,
  VivaConfig,
  VivaConfirmation
} from '../models/payment.model';

@Injectable({ providedIn: 'root' })
export class PaymentService {
  private api = inject(ApiService);

  processPayment(orderId: number, paymentTypeId: number, paymentData?: Record<string, any>): Observable<ProcessPaymentResponse> {
    return this.api.post<ProcessPaymentResponse>(`/orders/${orderId}/payment`, {
      paymentTypeId,
      ...paymentData
    });
  }

  // Braintree
  getBraintreeConfig(orderId: number): Observable<BraintreeConfig> {
    return this.api.get<BraintreeConfig>(`/payments/braintree/orders/${orderId}/config`);
  }

  executeBraintree(orderId: number, nonce: string): Observable<BraintreeExecuteResult> {
    // Returns only { success }; re-fetch the order to learn confirmed-vs-pending.
    // Declined transaction → HTTP 422; non-payable order → HTTP 400.
    return this.api.post<BraintreeExecuteResult>(`/payments/braintree/orders/${orderId}/execute`, { nonce });
  }

  // PayPal
  getPaypalConfig(orderId: number): Observable<PaypalConfig> {
    return this.api.get<PaypalConfig>(`/payments/paypal/orders/${orderId}/config`);
  }

  createPaypalOrder(orderId: number): Observable<PaypalCreateResult> {
    return this.api.get<PaypalCreateResult>(`/payments/paypal/orders/${orderId}/create`);
  }

  executePaypal(orderId: number, paypalOrderId: string): Observable<PaypalExecuteResult> {
    // Path orderId = our internal order id; body orderId = the PayPal order id.
    // Returns only { success }; the order must be re-fetched to learn whether
    // it ended up confirmed (3) or pending (2). success:false comes as HTTP 422.
    return this.api.post<PaypalExecuteResult>(`/payments/paypal/orders/${orderId}/execute`, { orderId: paypalOrderId });
  }

  // Multibanco
  getMultibancoConfig(orderId: number): Observable<MultibancoConfig> {
    return this.api.get<MultibancoConfig>(`/payments/mb/orders/${orderId}/config`);
  }

  // Cielo. The query param is snake_case `return_url` — that's the contract
  // the backend honors (see @dreambooks/api CieloService); a camelCase
  // `returnUrl` is silently ignored.
  getCieloConfig(orderId: number, returnUrl: string): Observable<CieloConfig> {
    return this.api.get<CieloConfig>(`/payments/cielo/orders/${orderId}/config`, {
      params: { return_url: returnUrl }
    });
  }

  // Boleto
  getBoletoConfig(orderId: number): Observable<BoletoConfig> {
    return this.api.get<BoletoConfig>(`/payments/boleto/orders/${orderId}/config`);
  }

  // Bank Transfer
  getBankTransferConfig(orderId: number): Observable<BankTransferConfig> {
    return this.api.get<BankTransferConfig>(`/payments/bank-transfer/orders/${orderId}/config`);
  }

  // Viva Wallet
  getVivaOrderCode(orderId: number): Observable<VivaConfig> {
    return this.api.get<VivaConfig>(`/payments/viva/orders/${orderId}`);
  }

  confirmViva(orderId: number, transactionId: string): Observable<VivaConfirmation> {
    return this.api.get<VivaConfirmation>(`/payments/viva/confirmation/${orderId}/${transactionId}`);
  }

  /**
   * Retire a dead Viva checkout after a failed/cancelled return so a retry
   * mints a fresh order code (instead of waiting on the delayed/absent
   * 1798 webhook). Idempotent, always HTTP 200. Message is one of:
   *  - "Payment aborted — request a new order code" → re-fetch a fresh code
   *  - "No active payment to abort" → re-fetch a fresh code anyway
   *  - "Order is not awaiting payment" → validating(2)/paid — DON'T retry, poll
   * Only call on the FAILURE/CANCEL return — never on success.
   */
  abortViva(orderId: number): Observable<{ message: string }> {
    return this.api.post<{ message: string }>(`/payments/viva/orders/${orderId}/abort`, {});
  }
}
